<script lang="ts" setup>
import { ref, watch } from "vue";
import VueMultiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import { useAlertStore } from "@/stores/alerts-store";
import type { IOrg } from "@/types";

interface Props {
  show: boolean;
  currentOrgId?: string; // The org that is inviting another org
}

const props = defineProps<Props>();

const emit = defineEmits<{
  "update:show": [value: boolean];
  invited: [];
}>();

const alertStore = useAlertStore();
const selectedOrganization = ref<IOrg | null>(null);
const customMessage = ref("");
const isSubmitting = ref(false);
const isLoadingOrgs = ref(false);
const error = ref("");

const availableOrganizations = ref<IOrg[]>([]);

// Watch for modal opening to reset form and load data
watch(
  () => props.show,
  (newShow) => {
    if (newShow) {
      resetForm();
      loadOrganizations();
    }
  }
);

function resetForm() {
  selectedOrganization.value = null;
  customMessage.value = "";
  error.value = "";
}

async function loadOrganizations() {
  isLoadingOrgs.value = true;
  error.value = "";
  try {
    const baseUrl = `${import.meta.env.VITE_API_URL}/orgs`;
    const allOrgs = (await fetchWrapper.get(baseUrl)) as any[];

    // Get existing partner clients for this org (including pending invitations)
    let existingPartnerClientIds: Set<string> = new Set();
    if (props.currentOrgId) {
      try {
        const partnerClientsUrl = `${baseUrl}/${props.currentOrgId}/partner-clients`;
        const partnerClients = (await fetchWrapper.get(partnerClientsUrl)) as any[];
        // Filter out all existing partnerships (active, pending, or inactive)
        existingPartnerClientIds = new Set(partnerClients.map((client: any) => client.id));
      } catch (err) {
        console.warn("Failed to load existing partner clients:", err);
        // Continue without filtering - better to show all orgs than fail completely
      }
    }

    // Map the response to IOrg interface and filter out current org and existing partners
    availableOrganizations.value = allOrgs
      .map((org: any) => ({
        orgId: org.orgId || org.OrgId,
        orgName: org.orgName || org.OrgName,
        canPublishProducts: org.canPublishProducts ?? org.CanPublishProducts ?? false,
        canPartnerWithOrgs: org.canPartnerWithOrgs ?? org.CanPartnerWithOrgs ?? false,
      }))
      .filter((org: IOrg) => {
        // Exclude current org
        if (props.currentOrgId && org.orgId === props.currentOrgId) {
          return false;
        }
        // Exclude existing partner clients
        if (existingPartnerClientIds.has(org.orgId)) {
          return false;
        }
        return true;
      });
  } catch (err) {
    console.error("Failed to load organizations:", err);
    error.value = "Failed to load organizations";
  } finally {
    isLoadingOrgs.value = false;
  }
}

function closeModal() {
  emit("update:show", false);
}

async function handleInvite() {
  error.value = "";

  if (!selectedOrganization.value) {
    error.value = "Please select an organization to invite";
    return;
  }

  if (!props.currentOrgId) {
    error.value = "Current organization ID is not available";
    return;
  }

  isSubmitting.value = true;

  try {
    const baseUrl = `${import.meta.env.VITE_API_URL}/partnerships`;

    // Send partnership invitation email using the new invite endpoint
    // PartnerOrgId is the current org (the one doing the inviting)
    // ClientOrgId is the org being invited as a client
    await fetchWrapper.post(`${baseUrl}/invite`, {
      partnerOrgId: props.currentOrgId,
      clientOrgId: selectedOrganization.value.orgId,
    });

    // Show success message
    // Note: If the org doesn't have an Owner/Admin, the invitation is still created
    // but email/notification may not be sent
    alertStore.addSuccess(
      "Partnership Invitation Created",
      `Partnership invitation created for ${selectedOrganization.value.orgName}. It will appear as "Pending" until accepted by an Owner or Admin of their organization.`
    );

    emit("invited");
    closeModal();
  } catch (err: any) {
    console.error("Failed to send partnership invitation:", err);

    // Check if this is an email warning (partnership created but email failed)
    if (err.message && err.message.includes("but email failed")) {
      // Show as warning - partnership was created successfully
      alertStore.addWarning(
        "Partnership Created (Email Not Sent)",
        err.message
      );
      emit("invited");
      closeModal();
    } else {
      // Actual error - partnership was not created
      error.value = err.message || "Failed to send partnership invitation. Please try again.";
    }
  } finally {
    isSubmitting.value = false;
  }
}

</script>

<template>
  <div
    class="modal fade"
    :class="{ show: show }"
    style="display: block"
    v-if="show"
    @click.self="closeModal"
  >
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title">
            <i class="bi bi-building-add me-2"></i>
            Add Partner Client Organization
          </h2>
          <button type="button" class="btn-close" @click="closeModal" :disabled="isSubmitting"></button>
        </div>

        <div class="modal-body">
          <div v-if="error" class="alert alert-danger" role="alert">
            <i class="bi bi-exclamation-triangle me-2"></i>
            {{ error }}
          </div>

          <div class="mb-4">
            <div class="d-flex align-items-center mb-3">
              <div class="bg-primary rounded-circle p-2 me-3">
                <i class="bi bi-handshake text-white"></i>
              </div>
              <div>
                <h3 class="mb-1">Add Partner Client</h3>
                <p class="text-muted mb-0 small">
                  Select an organization from the system to add as a partner client
                </p>
              </div>
            </div>
          </div>

          <!-- Organization Selection -->
          <div class="mb-3">
            <label class="form-label"
              >Select Organization <span class="text-danger">*</span></label
            >
            <VueMultiselect
              v-model="selectedOrganization"
              :options="availableOrganizations"
              :custom-label="(opt: IOrg) => opt.orgName"
              :placeholder="
                isLoadingOrgs
                  ? 'Loading organizations...'
                  : 'Search and select an organization'
              "
              label="orgName"
              track-by="orgId"
              :loading="isLoadingOrgs"
              :searchable="true"
              :allow-empty="true"
              :disabled="isSubmitting"
              select-label=""
              deselect-label=""
            >
              <template #option="{ option }">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="fw-bold">{{ option.orgName }}</div>
                  <div class="d-flex gap-1">
                    <span
                      v-if="option.canPublishProducts"
                      class="badge bg-info"
                      title="Can publish products"
                    >
                      <i class="bi bi-box-seam"></i>
                    </span>
                    <span
                      v-if="option.canPartnerWithOrgs"
                      class="badge bg-success"
                      title="Can partner with organizations"
                    >
                      <i class="bi bi-diagram-3"></i>
                    </span>
                  </div>
                </div>
              </template>

              <template #singleLabel="{ option }">
                <span class="fw-bold">{{ option.orgName }}</span>
              </template>

              <template #noResult>
                <div class="text-center py-2 text-muted">
                  <i class="bi bi-search me-1"></i>
                  No organizations found
                </div>
              </template>

              <template #noOptions>
                <div class="text-center py-2 text-muted" v-if="!isLoadingOrgs">
                  <i class="bi bi-building me-1"></i>
                  No organizations available
                </div>
              </template>
            </VueMultiselect>
          </div>

          <!-- Selected Organization Details -->
          <div v-if="selectedOrganization" class="mb-3">
            <div class="card bg-body-tertiary border-0">
              <div class="card-body py-3">
                <div class="d-flex align-items-center justify-content-between">
                  <p class="fw-bold mb-0">{{ selectedOrganization.orgName }}</p>
                  <div class="d-flex gap-2">
                    <span
                      v-if="selectedOrganization.canPublishProducts"
                      class="badge bg-info"
                      title="Can publish products"
                      >Publisher</span
                    >
                    <span
                      v-if="selectedOrganization.canPartnerWithOrgs"
                      class="badge bg-success"
                      title="Can partner with organizations"
                      >Partner Enabled</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- What happens next -->
          <div class="alert alert-info mb-0" role="alert">
            <div class="d-flex">
              <div class="me-2">
                <i class="bi bi-info-circle"></i>
              </div>
              <div>
                <strong>What happens next:</strong>
                <ol class="mb-0 mt-1 small">
                  <li>
                    A partnership invitation will be created for
                    <strong v-if="selectedOrganization">{{
                      selectedOrganization.orgName
                    }}</strong
                    ><span v-else>the selected organization</span>
                  </li>
                  <li>
                    If they have an Owner or Admin user, an email and notification will be sent
                  </li>
                  <li>
                    The partnership will appear as "Pending" until accepted
                  </li>
                  <li>Once accepted, they will appear in your active partner clients list</li>
                  <li>You'll be able to view their projects and activity</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="bb-btn-cancel"
            @click="closeModal"
            :disabled="isSubmitting"
          >
            Cancel
          </button>
          <button
            type="button"
            class="bb-btn-primary-cta"
            @click="handleInvite"
            :disabled="isSubmitting || !selectedOrganization"
          >
            <span
              v-if="isSubmitting"
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            <i v-else class="bi bi-envelope-paper me-2"></i>
            {{ isSubmitting ? "Sending Invitation..." : "Send Invitation" }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show" v-if="show" @click="closeModal"></div>
</template>

<!-- VueMultiselect theming is global — see App/src/scss/styles.scss (.multiselect*) -->
